import { useEffect, useState } from "react";
import { getEbooks } from "../../app/api";
import EbookCard, { EbookProps } from "./ebook-card";
import { Pagination } from "antd";
import { useTranslation } from 'react-i18next';
import FloatingShapes from "../common/FloatingShapes";

const EbookList = () => {
  const { t } = useTranslation();
  const [ebooks, setEbooks] = useState<EbookProps[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 8;

  useEffect(() => {
    setLoading(true);
    getEbooks({ offset: (page - 1) * pageSize, limit: pageSize })
      .then((res) => {
        setEbooks(res.data.results);
        setTotal(res.data.total);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Dark hero header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/15 blur-[100px]" />
        </div>
        <FloatingShapes variant="alt" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-secondary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
              {t('ebook.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              {t('ebook.title')}
            </h1>
            <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
              {t('ebook.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Book grid */}
      <div className="bg-neutral-50 py-16 text-neutral-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {ebooks.length === 0 && !loading ? (
            <div className="text-center py-20">
              <p className="text-neutral-500 text-lg">{t('ebook.noBooks')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                {ebooks.map((ebook) => (
                  <EbookCard key={ebook._id || ebook.id} {...ebook} />
                ))}
              </div>

              {total > pageSize && (
                <div className="flex justify-center mt-12">
                  <Pagination
                    current={page}
                    total={total}
                    pageSize={pageSize}
                    onChange={(p) => setPage(p)}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EbookList;
